import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IsDoctorAssistant, IsDoctorAssistantOnly, IsHIVAssistant, IsHIVDoctor, IsHIVUser, isProgramExist } from '@helpers/permissions';
import { IRouteInfo } from '@interfaces/i-route-info';
import { NavigationModel } from '@models/navigation.model';
import { googleAnalyticsTracker } from '@helpers/googleAnalytics';
import { PermissionService } from '@services/permission.service';
import { DoctorsService } from '@services/doctors/doctors.service';
import { NormalDoctorPermission } from 'src/app/_constants/permissions';

const misc: any = {
  sidebar_mini_active: true,
};

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})
export class SidebarComponent implements OnInit {
  isCollapsed: boolean = true;
  menuItems: IRouteInfo[] = [];
  doctorCarnivitaPermission: boolean = true;
  navigationModel: NavigationModel;
  isSideBarCollapsed: boolean = false;
  complaintEmailsCC: string = '';
  complaintEmailTo: string = '';

  constructor(private router: Router, public permissionService: PermissionService, private doctorService: DoctorsService) {
    this.navigationModel = new NavigationModel();
  }

  ngOnInit(): void {
    this.menuItems = this.filterMenuItems(this.navigationModel.routes);

    this.hideHomeForAssistants();
    this.hidePatientAnalysisForNormalAssistant();
    this.hideAssistantManagementForHivDoctors();
    this.hideClinicFinancesForHivDoctors();
    this.router.events.subscribe((event: any) => {
      this.isCollapsed = true;
    });
  }

  private hidePatientAnalysisForNormalAssistant() {
    if (IsDoctorAssistantOnly()) {
      this.menuItems = this.menuItems.filter(a => a.path !== 'patients-analysis');
    }
  }

  private hideHomeForAssistants() {
    if (IsDoctorAssistant()) {
      this.menuItems = this.menuItems.filter(a => a.path !== 'home');
    }
  }

  private hideAssistantManagementForHivDoctors() {
    if (IsHIVDoctor() || IsHIVAssistant()) {
      this.menuItems = this.menuItems.filter(a => a.path !== 'assistants-management');
    }
  }

  private hideClinicFinancesForHivDoctors() {
    if (IsHIVUser()) {
      this.menuItems = this.menuItems.filter(a => a.path !== 'clinic-finances' && a.path !== 'clinic-analysis');
    }
  }

  private filterMenuItems(items: IRouteInfo[]): IRouteInfo[] {
    return items.filter(item => {
      const hasPermission = this.hasPermission(item);
      if (item.children) {
        item.children = this.filterMenuItems(item.children);
      }
      return hasPermission;
    });
  }

  // sidebar.component.ts
  private hasPermission(item: IRouteInfo): boolean {
    // Helper: Check if NormalDoctorPermission is the only permission
    const isNormalDoctorOnly =
      Array.isArray(item.permission) &&
      item.permission.length === 1 &&
      item.permission[0] === NormalDoctorPermission;

    // Case 1: No permissions required (public items)
    if (!item.permission) {
      if (this.permissionService.isAssistant()) {
        return item.isAssistant === true;
      }
      return true;
    }

    // Case 2: Only NormalDoctorPermission required
    if (isNormalDoctorOnly) {
      // If assistants allowed, show for assistants OR users with NormalDoctorPermission
      if (item.isAssistant && this.permissionService.isAssistant()) {
        return true;
      }

      // Check if user has NormalDoctorPermission
      return this.permissionService.hasPermission(NormalDoctorPermission);
    }

    // Case 3: Other specific permissions
    if (Array.isArray(item.permission)) {
      return item.permission.some(p => this.permissionService.hasPermission(p));
    }

    // Type guard
    if (!item.permission) {
      return false;
    }

    return this.permissionService.hasPermission(item.permission);
  }


  onMouseEnterSidenav() {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      // document.body.classList.add('g-sidenav-show');
    }
  }

  onMouseLeaveSidenav() {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      document.body.classList.remove('g-sidenav-show');
    }
  }

  minimizeSidebar() {
    const sidenavToggler =
      document.getElementsByClassName('sidenav-toggler')[0];
    const body = document.getElementsByTagName('body')[0];
    if (body.classList.contains('g-sidenav-pinned')) {
      misc.sidebar_mini_active = true;
    } else {
      misc.sidebar_mini_active = false;
    }
    if (misc.sidebar_mini_active === true) {
      body.classList.remove('g-sidenav-pinned');
      body.classList.add('g-sidenav-hidden');
      sidenavToggler.classList.remove('active');
      misc.sidebar_mini_active = false;
      this.isSideBarCollapsed = true;
    } else {
      this.isSideBarCollapsed = false;
      body.classList.add('g-sidenav-pinned');
      body.classList.remove('g-sidenav-hidden');
      sidenavToggler.classList.add('active');
      misc.sidebar_mini_active = true;
    }
  }

  applyGoogleAnalytics(key: string) {
    googleAnalyticsTracker(key)
  }

  getDoctorComplaintEmails(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.doctorService.getDoctorComplaintEmails().subscribe(
        (res) => {
          this.complaintEmailsCC = res.data.doctorComplaintCC;
          this.complaintEmailTo = res.data.doctorComplaintTo;
          resolve();
        },
        (err) => {
          console.error('Error fetching complaint emails:', err);
          reject(err);
        }
      );
    });
  }

  async reportProblem() {
    await this.getDoctorComplaintEmails()
    const doctorName = localStorage.getItem('fullName')
    const subject = `Issue with Dr ${doctorName}`;
    const toEmail = this.complaintEmailTo;
    const mailtoLink = `mailto:${toEmail}?subject=${encodeURIComponent(subject)}`;
    window.location.href = mailtoLink;
  }

  validateAdRole(): boolean {
    return isProgramExist('Dapagliflozen') || isProgramExist('Gliptus2') || isProgramExist('Genuphil For Woman');
  }

}
